import Hls from 'hls.js';
import { KinesisVideo } from '@aws-sdk/client-kinesis-video';
import { KinesisVideoArchivedMedia } from '@aws-sdk/client-kinesis-video-archived-media';
import {
  CameraStatus,
  CommonErrorCodes,
  KVSErrorCodes,
  PlayerStatus,
} from '../../globals/enums';
import hlsConfig from '@/config/hls.config';
import EventBus from '@/utils/eventBus';
import dayjs from 'dayjs';
import {
  checkFragLoadLevelError,
  fetchFragStatus,
  noRetryFnGetHLSStreamingSessionURL,
  retry,
  uuidv4,
} from '@/utils/helpers';
import { enableLogs } from './hls.logger';
import {
  GetHLSStreamingSessionURLRetryInterval,
  VDA4ErrorMaxDuration,
} from '@/globals/constants';

import { AppAbortController } from '@/utils/abortController';
import {
  checkFragExistence,
  getAllFragmentHoles,
  getFragmentByTimestamp,
  hlsParser,
  mapFragments,
} from '@/utils/video.util';
import { tracer } from '@/utils/print';
import Vue from 'vue';
import { pLoader } from '@/utils/customHls';

export default class KVService {
  playerElement = null;
  player = null;
  signalKey = null;

  /**
   * Constructor
   */
  constructor() {
    this.initProperties();
    this.handlePlayerElementError = this.handlePlayerElementError.bind(this);
  }

  /*
   * Initialize properties
   */
  initProperties() {
    this.debug = false;
    this.stopOnError = false;
    this.kinesisVideo = null;
    this.kinesisVideoArchivedContent = null;
    this._kvsOptions = null;
    this.lastFragBuffered = null;
    this.lastFragChanged = null;
    this.autoRecoverError = true;
    this.recoverSwapAudioCodecDate = null;
    this.retrySwapAudioCodecCount = 0;
    this.recoverDecodingErrorDate = null;
    this.firstRecoverVDAError4 = null;
    this.recoverVDAError4Timeout = null;
    this.recoverVDAError4Delay = 5000;
    this.noFragChangeDelay = 30000;
    this.levelLoadNoFragChangeCount = 0;
    this.levelLoadNoFragChangeMax = 20;
    this.VDAError4Count = 0;
    this.startTimestamp = null;
    this.id = null;
    this.streamName = null;
    this.eventKey = null;
    this.camera = null;
    this.isLive = true;
    this.device = null;
  }

  // customized log
  log = (message, ...optionalParams) =>
    this.debug &&
    // eslint-disable-next-line no-console
    console.log(`[${this.camera.name}][HLS] -> `, message, ...optionalParams);

  warn = (message, ...optionalParams) =>
    // eslint-disable-next-line no-console
    console.warn(`[${this.camera.name}][HLS] -> `, message, ...optionalParams);

  // customized error log
  error = (message, ...optionalParams) =>
    // eslint-disable-next-line no-console
    console.error(`[${this.camera.name}][HLS] -> `, message, ...optionalParams);

  /**
   * Run Kinesis Video Streams to get a stream URL
   * @param {Object} options Kinesis Video options
   * @param {Object} data
   * @param {Object} events
   */
  async run(options, data, eventKey) {
    const disposeReason = `[${data.camera.name}] run with timestamp ${dayjs(
      this.startTimestamp
    ).format('HH:mm:ss')}`;
    this.dispose(disposeReason);

    this.setupRun(options, data, eventKey);

    // intialize abortable controller
    this.signalKey = `kvs.service:${uuidv4()}`;
    const { signal } = AppAbortController.newController(this.signalKey);

    // Step 2: Get a data endpoint for the stream
    this.log('Fetching data endpoint');
    // Get the data endpoint for the stream
    let endpointRes = null;
    try {
      endpointRes = await this.getDataEndpoint(
        {
          StreamName: data.streamName,
          APIName: this.getAPIName(data.protocol),
        },
        signal
      );
    } catch (error) {
      this.handleGetDataEndpointError(error);
      return;
    }

    this.log('Data endpoint: ' + endpointRes.DataEndpoint);
    // v3 clients are immutable — recreate with the data endpoint returned by KVS
    this.kinesisVideoArchivedContent = new KinesisVideoArchivedMedia({
      ...this._kvsOptions,
      endpoint: endpointRes.DataEndpoint,
    });

    // Step 3: Get a Streaming Session URL
    const consoleInfo = 'Fetching ' + data.protocol + ' Streaming Session URL';
    try {
      this.log(
        data.playbackMode,
        'START: ',
        dayjs(data.startTimestamp).format('HH:mm:ss'),
        'END: ',
        dayjs(data.endTimestamp).format('HH:mm:ss')
      );
      this.log(consoleInfo);

      const params = this.constructParams(data);

      if (!data.skipFragCheck) {
        await this.handleFragmentExist(params, data, signal);
      }

      const hlsRes = await this.getHLSStreamingSessionURLWithRetry(
        params,
        data.notSync,
        signal,
        data.startPosition
      );
      this.log('HLS Streaming Session URL: ' + hlsRes.HLSStreamingSessionURL);
      this.emitSuccessEvents(hlsRes, data.id, data.startPosition, signal);

      this.lastFragChangeTime = 0;
    } catch (error) {
      this.dispatchKVSError(error, {
        notSync: data.notSync,
        startPosition: data.startPosition,
      });
    }
  }

  /**
   * Dispatch KVS error
   * @param {Error} error
   */
  dispatchKVSError(error, { notSync, retry, startPosition } = {}) {
    const isNoFragError =
      error?.message && error.message.includes('No fragments found ');
    if (isNoFragError) {
      this.log('[ERROR] No fragments found');
      this.emitKVSError(
        error,
        KVSErrorCodes.NO_FRAGMENT_FOUND,
        retry,
        notSync,
        startPosition
      );
    } else if (error instanceof DOMException && error.name === 'AbortError') {
      this.emitKVSError(error, KVSErrorCodes.OperationCanceled, retry);
    } else {
      this.emitKVSError(error, KVSErrorCodes.GET_STREAMING_SESSION_URL, retry);
    }
  }

  /**
   * Check whether fragment exist before starting video stream
   */
  async handleFragmentExist(params, data, signal) {
    if (data.playbackMode !== 'LIVE') {
      const clonedParams = structuredClone(params);
      clonedParams.HLSFragmentSelector.TimestampRange.EndTimestamp = dayjs(
        data.startTimestamp
      )
        .add(13, 'seconds')
        .toDate();
      const response = await retry(
        () => this.checkFragmentExist(clonedParams, signal),
        {
          retries: 4,
          retryIntervalMs: GetHLSStreamingSessionURLRetryInterval,
          noRetryFn: noRetryFnGetHLSStreamingSessionURL,
          onError: ({ error, retries }) => {
            if (retries === 4) {
              this.dispatchKVSError(error, {
                notSync: data.notSync,
                retry: true,
                startPosition: data.startPosition,
              });
            }
          },
          signal,
        }
      );
      return response;
    }
  }

  /**
   * Call getHLSStreamingSessionURL with retry attempt
   */ e;
  async getHLSStreamingSessionURLWithRetry(
    params,
    notSync,
    signal,
    startPosition
  ) {
    return await retry(() => this.getHLSStreamingSessionURL(params, signal), {
      retries: 4,
      retryIntervalMs: GetHLSStreamingSessionURLRetryInterval,
      noRetryFn: noRetryFnGetHLSStreamingSessionURL,
      onError: ({ error, retries }) => {
        if (retries === 4) {
          this.dispatchKVSError(error, { notSync, retry: true, startPosition });
        }
      },
      signal,
    });
  }

  /**
   * Emit KVS error
   */
  emitKVSError(error, errorCode, isRetry, notSync, startPosition) {
    EventBus.emit(this.eventKey, {
      type: 'kvsError',
      payload: error,
      meta: {
        id: this.id,
        retry: isRetry,
        errorCode,
        notSync,
        stopOnError: this.stopOnError,
        startPosition,
      },
    });
  }

  /**
   * Emit success event
   * @param {*} eventKey
   * @param {*} hlsRes
   * @param {*} id
   * @param {*} startPosition
   */
  emitSuccessEvents(hlsRes, id, startPosition, signal) {
    if (signal?.aborted) {
      return;
    }
    EventBus.emit(this.eventKey, {
      type: 'kvsStatus',
      payload: { status: CameraStatus.Connected },
      meta: { id },
    });
    EventBus.emit(this.eventKey, {
      type: 'kvsSuccess',
      payload: { ...hlsRes, startPosition },
      meta: { id, signal },
    });
  }

  /**
   * Initialize data for tunning KVS
   * @param {*} options
   * @param {*} data
   * @param {*} eventKey
   */
  setupRun(options, data, eventKey) {
    this.startTimestamp = data.startTimestamp;
    this.initProperties();
    this.camera = data.camera;
    this.debug = data.debug || this.debug;
    this.isLive = data.playbackMode === 'LIVE';
    this.device = data.device;
    this.stopOnError = data.stopOnError;
    this.eventKey = eventKey;
    this.id = this.camera.id;
    this.streamName = data.streamName;
    this.warn(
      `[${dayjs().format('HH:mm:ss')}] Running KVS with startTimestamp: ${dayjs(
        data.startTimestamp
      ).format('HH:mm:ss')} and endTimestamp: ${dayjs(data.endTimestamp).format(
        'HH:mm:ss'
      )}`
    );
    this.emitPlayerStatus(CameraStatus.Loading);

    this._kvsOptions = {
      region: options.region,
      credentials: {
        accessKeyId: options.accessKeyId,
        secretAccessKey: options.secretAccessKey,
        sessionToken: options.sessionToken,
      },
      ...(options.endpoint ? { endpoint: options.endpoint } : {}),
    };
    this.kinesisVideo = new KinesisVideo(this._kvsOptions);
    this.kinesisVideoArchivedContent = new KinesisVideoArchivedMedia(
      this._kvsOptions
    );
  }

  /**
   * Get API name based on protocol
   * @param {*} protocol
   * @returns
   */
  getAPIName(protocol) {
    return protocol === 'DASH'
      ? 'GET_DASH_STREAMING_SESSION_URL'
      : 'GET_HLS_STREAMING_SESSION_URL';
  }

  /**
   * Construct params for getHLSStreamingSessionURL
   * @param {*} data
   * @returns
   */
  constructParams(data) {
    const params = {
      StreamName: data.streamName,
      PlaybackMode: data.playbackMode,
      HLSFragmentSelector: {
        FragmentSelectorType: data.fragmentSelectorType,
        TimestampRange:
          data.playbackMode === 'LIVE'
            ? undefined
            : {
                StartTimestamp: new Date(data.startTimestamp),
                EndTimestamp: new Date(data.endTimestamp),
              },
      },
      ContainerFormat: data.containerFormat,
      DiscontinuityMode: data.discontinuityMode,
      DisplayFragmentTimestamp: data.displayFragmentTimestamp,
      MaxMediaPlaylistFragmentResults: data.maxResults,
      Expires: data.expires,
    };
    return params;
  }

  /**
   * Handle get data endpoint error
   * @param {*} error
   * @returns
   */
  handleGetDataEndpointError(error) {
    if (error instanceof DOMException && error.name === 'AbortError') {
      // intentional abort triggered by dispose/navigation — not an error condition.
      this.dispatchKVSError(error);
      return;
    }
    this.error(
      `[${dayjs().format('HH:mm:ss')}] Catch get endpoint error`,
      error.message,
      error
    );
    if (
      error?.message &&
      error.message.includes(
        'The security token included in the request is expired'
      )
    ) {
      EventBus.emit(this.eventKey, {
        type: 'kvsError',
        payload: error,
        meta: {
          id: this.id,
          errorCode: KVSErrorCodes.GET_DATA_ENDPOINT_TOKEN_EXP,
        },
      });
      return;
    }
    EventBus.emit(this.eventKey, {
      type: 'kvsError',
      payload: error,
      meta: { id: this.id, errorCode: KVSErrorCodes.GET_DATA_ENDPOINT },
    });
  }

  /**
   * Get kinesis video data endpoint
   */
  getDataEndpoint(params, signal) {
    return new Promise((resolve, reject) => {
      if (signal?.aborted) {
        reject(new DOMException('Aborted', 'AbortError'));
        return;
      }
      const abortHandler = () =>
        reject(new DOMException('Aborted', 'AbortError'));
      signal?.addEventListener('abort', abortHandler);

      this.kinesisVideo.getDataEndpoint(params).then(
        (response) => {
          signal?.removeEventListener('abort', abortHandler);
          if (signal?.aborted) {
            reject(new DOMException('Aborted', 'AbortError'));
          } else {
            resolve(response);
          }
        },
        (err) => {
          signal?.removeEventListener('abort', abortHandler);
          if (signal?.aborted) {
            reject(new DOMException('Aborted', 'AbortError'));
          } else {
            reject(err);
          }
        }
      );
    });
  }

  /**
   * Validates the parameters for the getHLSStreamingSessionURL function.
   * Ensures that when PlaybackMode is 'ON_DEMAND', both StartTimestamp and EndTimestamp
   * within HLSFragmentSelector.TimestampRange are provided.
   *
   * @param {Object} params - The parameters to validate.
   * @param {string} params.StreamName - The name of the stream.
   * @param {string} params.PlaybackMode - The playback mode, should be 'LIVE' or 'ON_DEMAND'.
   * @param {Object} params.HLSFragmentSelector - The HLS fragment selector object.
   * @param {string} params.HLSFragmentSelector.FragmentSelectorType - The type of fragment selector.
   * @param {Object} params.HLSFragmentSelector.TimestampRange - The timestamp range object.
   * @param {Date} params.HLSFragmentSelector.TimestampRange.StartTimestamp - The start timestamp.
   * @param {Date} params.HLSFragmentSelector.TimestampRange.EndTimestamp - The end timestamp.
   * @param {string} params.ContainerFormat - The container format, e.g., 'MPEG_TS'.
   * @param {string} params.DiscontinuityMode - The discontinuity mode.
   * @param {string} params.DisplayFragmentTimestamp - The display fragment timestamp mode.
   * @param {number} params.MaxMediaPlaylistFragmentResults - The maximum number of media playlist fragment results.
   * @param {number} params.Expires - The expiration time in seconds.
   *
   * @throws Will throw an error if StartTimestamp or EndTimestamp is missing when PlaybackMode is 'ON_DEMAND'.
   */
  validateHLSStreamingSessionParams(params) {
    const { PlaybackMode, HLSFragmentSelector } = params;

    // check if PlaybackMode is ON_DEMAND
    if (PlaybackMode === 'ON_DEMAND') {
      const { TimestampRange } = HLSFragmentSelector;

      if (TimestampRange) {
        const { StartTimestamp, EndTimestamp } = TimestampRange;

        // check if StartTimestamp is missing
        if (!StartTimestamp) {
          throw new Error(
            'StartTimestamp must have a value when PlaybackMode is ON_DEMAND.'
          );
        }

        // check if EndTimestamp is missing
        if (!EndTimestamp) {
          throw new Error(
            'EndTimestamp must have a value when PlaybackMode is ON_DEMAND.'
          );
        }
      } else {
        throw new Error(
          'TimestampRange must be provided when PlaybackMode is ON_DEMAND.'
        );
      }
    }
  }

  /**
   * Get HLS streaming session URL
   *
   * @param {Object} params - The parameters to validate.
   * @param {string} params.StreamName - The name of the stream.
   * @param {string} params.PlaybackMode - The playback mode, should be 'LIVE' or 'ON_DEMAND'.
   * @param {Object} params.HLSFragmentSelector - The HLS fragment selector object.
   * @param {string} params.HLSFragmentSelector.FragmentSelectorType - The type of fragment selector.
   * @param {Object} params.HLSFragmentSelector.TimestampRange - The timestamp range object.
   * @param {Date} params.HLSFragmentSelector.TimestampRange.StartTimestamp - The start timestamp.
   * @param {Date} params.HLSFragmentSelector.TimestampRange.EndTimestamp - The end timestamp.
   * @param {string} params.ContainerFormat - The container format, e.g., 'MPEG_TS'.
   * @param {string} params.DiscontinuityMode - The discontinuity mode.
   * @param {string} params.DisplayFragmentTimestamp - The display fragment timestamp mode.
   * @param {number} params.MaxMediaPlaylistFragmentResults - The maximum number of media playlist fragment results.
   * @param {number} params.Expires - The expiration time in seconds.
   *
   */
  getHLSStreamingSessionURL(params, signal) {
    this.validateHLSStreamingSessionParams(params);
    return new Promise((resolve, reject) => {
      if (signal?.aborted) {
        reject(new DOMException('Aborted', 'AbortError'));
        return;
      }
      const abortHandler = () =>
        reject(new DOMException('Aborted', 'AbortError'));
      signal?.addEventListener('abort', abortHandler);

      this.kinesisVideoArchivedContent.getHLSStreamingSessionURL(params).then(
        (response) => {
          signal?.removeEventListener('abort', abortHandler);
          if (signal?.aborted) {
            reject(new DOMException('Aborted', 'AbortError'));
          } else {
            resolve(response);
          }
        },
        (err) => {
          signal?.removeEventListener('abort', abortHandler);
          if (signal?.aborted) {
            reject(new DOMException('Aborted', 'AbortError'));
          } else {
            reject(err);
          }
        }
      );
    });
  }

  /**
   * Check whether HLS fragment exist in given params but in short time forward (3 seconds)
   * @param {*} params
   * @returns
   */
  checkFragmentExist(params, signal) {
    return this.getHLSStreamingSessionURL(params, signal);
  }

  /**
   * This function will fetch the m3u8 file and parse it to get fragment list.
   * Get fragment list using `getHLSStreamingSessionURL` response data. `response.HLSStreamingSessionURL` is the url to get m3u8 file.
   * It will replace the url with `getHLSMediaPlaylist.m3u8` to get fragment list.
   * This is not recommanded to use this way to get fragment list. It is better to use `ListFragments` method. But this is a workaround.
   * NOTE: We will change this method to use `ListFragments` method in future.
   */
  async getFragmentList(startTimestamp, endTimestamp, signal) {
    // Invalid Date (e.g. from dayjs.add(undefined, 'seconds')) would be serialized
    // as null by SDK v3 → API 400. Treat as "no fragments" to preserve callers' behaviour.
    const isValid = (d) => d instanceof Date && !isNaN(d.getTime());
    if (!isValid(startTimestamp) || !isValid(endTimestamp)) {
      return [];
    }

    const params = {
      StreamName: this.streamName,
      PlaybackMode: 'ON_DEMAND',
      HLSFragmentSelector: {
        FragmentSelectorType: 'PRODUCER_TIMESTAMP',
        TimestampRange: {
          StartTimestamp: startTimestamp,
          EndTimestamp: endTimestamp,
        },
      },
      ContainerFormat: 'MPEG_TS',
      DiscontinuityMode: 'ON_DISCONTINUITY',
      DisplayFragmentTimestamp: 'ALWAYS',
      MaxMediaPlaylistFragmentResults: 100,
      Expires: 600,
    };

    try {
      let retries = 0;
      let response;
      while (retries < 3) {
        if (signal?.aborted) {
          throw new DOMException('Aborted', 'AbortError');
        }
        response = await this.getHLSStreamingSessionURL(params, signal);
        if (response.HLSStreamingSessionURL) {
          break;
        }
        retries++;
        if (retries < 3) {
          await new Promise((resolve) => setTimeout(resolve, 1000)); // Wait 1 second before retrying
        }
      }
      if (!response?.HLSStreamingSessionURL) {
        tracer('getFragmentList/error/!response?.HLSStreamingSessionURL', {
          params,
          signal,
        });
        return [];
      }
      return hlsParser(response.HLSStreamingSessionURL, signal);
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') {
        throw error;
      } else {
        console.error(error);
      }
      return [];
    }
  }

  /**
   * Check any fragments for backbuffer `seekStartPosition - startTimestamp` exist or not. If exit returns `startTimestampWithBuffer`. If not exist, check for startTimestamp fragment exist or not.
   * If not exist, `startTimestamp`.
   */
  async checkBackBufferFragmentExist(startTimestamp, startPosition, signal) {
    // endTimestamp is `startPosition` seconds forward from startTimestamp
    const startTime = dayjs(startTimestamp);
    const endTimestamp = startTime.add(startPosition, 'seconds').toDate();

    const startTimestampWithBuffer = startTime.subtract(
      startPosition,
      'seconds'
    );
    let backbuffer = true;
    let fragments = await this.getFragmentList(
      startTimestampWithBuffer.toDate(),
      endTimestamp,
      signal
    );

    if (fragments.length === 0) {
      backbuffer = false;
      fragments = await this.getFragmentList(
        startTime.toDate(),
        endTimestamp,
        signal
      );
    }

    // startTimestamp with buffer is `startTimestamp - startPosition` seconds
    const startTimestampOfBackBuffer = startTimestampWithBuffer.valueOf();

    // check for start fragment exist or not in fragments list
    const startFragExist = checkFragExistence(fragments, startTimestamp, 4);

    // check for start backbuffer fragment exist or not in fragments list and it should be before startTimestamp of backbuffer fragment list
    // if it is not exist, then it is not backbuffer.
    const startBackbufferExist =
      backbuffer &&
      checkFragExistence(fragments, startTimestampOfBackBuffer, 4);

    // check whether the backbuffer can be played as normal the the first fragment
    // whether the first fragment from backbuffer has the same discontinuity sequence the fragmnet in the `startPosition`
    // there is a case that discontinuity sequence is not the same and the CPD is different therefore we avoid using backbuffer
    const startPositionFrag = getFragmentByTimestamp(
      fragments,
      startTime.valueOf()
    );

    // check discontinuity sequence and existence of fragment in backbuffer
    const useBackBuffer =
      fragments?.length &&
      fragments?.[0]?.cc === fragments?.[startPositionFrag?.fragment?.sn]?.cc &&
      startBackbufferExist;
    tracer('checkBackBufferFragmentExist', {
      startTimestamp: dayjs(startTimestamp).format('HH:mm:ss'),
      startTimestampWithBuffer: dayjs(startTimestampWithBuffer).format(
        'HH:mm:ss'
      ),
      endTimestamp: dayjs(endTimestamp).format('HH:mm:ss'),
      useBackBuffer,
      fragments,
      startPositionFrag: {
        ...startPositionFrag?.fragment,
      },
      firstFragmentCC: fragments?.[0]?.cc,
      startPositionFragCC: fragments?.[startPositionFrag?.fragment?.sn]?.cc,
    });
    return {
      startTimestamp: startTimestampWithBuffer.valueOf(),
      fragments,
      backbuffer,
      startFragExist,
      startBackbufferExist,
      startPositionFrag,
      useBackBuffer,
    };
  }

  /**
   * Play a stream using HLS.js
   * @param {String} url
   * @param {Element} playerElement
   * @returns
   */
  playHls(url, playerElement, eventKey, id, startPosition, signal) {
    if (!playerElement) {
      throw Error('Player element is not defined');
    }
    if (signal?.aborted) {
      return;
    }
    this.setupPlayer(playerElement, eventKey, id, startPosition);

    this.setupPlayerEvents(url, startPosition, signal);

    // attach to the player
    this.player.attachMedia(playerElement);
  }

  /**
   * Setup player events
   * @param {*} url
   * @param {*} startPosition
   */
  setupPlayerEvents(url, startPosition, signal) {
    this.player.on(Hls.Events.FRAG_BUFFERED, this.onFragBuffered.bind(this));
    this.player.on(Hls.Events.LEVEL_LOADED, this.onLevelLoaded.bind(this));
    this.player.on(Hls.Events.FRAG_LOADED, this.onFragLoaded.bind(this));
    this.player.on(Hls.Events.FRAG_CHANGED, this.onFragChanged.bind(this));
    this.player.on(Hls.Events.ERROR, this.onError.bind(this));

    this.player.once(Hls.Events.MEDIA_ATTACHED, () => {
      this.log('HLS Media Attached');
      this.player.loadSource(url);
      this.player.once(Hls.Events.MANIFEST_PARSED, (event, data) => {
        if (signal?.aborted) {
          this.player.stopLoad();
          return;
        }
        this.log('Hls.Events.MANIFEST_PARSED ', event, data);
        if (startPosition) {
          // this.player.startPosition = startPosition;

          this.player.startLoad(startPosition);
        }
        this.emitPlayerStatus(CameraStatus.Connected);
        this.emitPlayerStatus(CameraStatus.Loaded);
        this.log(
          'manifest loaded, found ' + data.levels.length + ' quality level',
          data
        );
      });
    });
  }

  /**
   * Handle FRAG_BUFFERED player event
   * @param {*} event
   * @param {*} data
   */
  onFragBuffered(event, data) {
    this.log('FRAG_BUFFERED', event, data);
    const prevFrag = { ...this.lastFragBuffered };
    const frag = data.frag;
    const discontinuity =
      prevFrag && frag.cc !== prevFrag.cc && frag.cc > prevFrag.cc;
    this.lastFragBuffered = { ...frag };
    this.emitPlayerStatus(PlayerStatus.FragmentBuffered, { frag });

    if (discontinuity) {
      this.log('Discontinuity detected on FRAG_BUFFERED');
      // this.emitPlayerStatus(PlayerStatus.Discontinuity, { frag, prevFrag });
    }
  }

  /**
   * Handle LEVEL_LOADED player event
   * @param {*} event
   * @param {*} data
   * @returns
   */
  onLevelLoaded(event, data) {
    const fragments = mapFragments(data.details?.fragments || []);
    if (!this.isLive) {
      fragments
        .filter((f) => f.discontinuity)
        .forEach((f) => {
          fetchFragStatus(f.fetchUrl).then((status) => {
            const codecChanged = status === PlayerStatus.CodecIsNotTheSame;
            f.codecChanged = codecChanged;
            Vue.set(f, 'codecChanged', codecChanged);
            // add fragment status changed
            this.emitPlayerStatus(PlayerStatus.FragmentStatusChanged, {
              frag: f,
            });
          });
        });
    }

    const now = performance.now();
    this.log('LEVEL_LOADED ', event, data.frag);
    this.levelLoadNoFragChangeCount += 1;

    if (now - this.lastFragChangeTime > this.noFragChangeDelay && this.isLive) {
      if (this.levelLoadNoFragChangeCount > this.levelLoadNoFragChangeMax) {
        this.emitPlayerStatus(PlayerStatus.NoFragmentChanged);
      }
      return;
    }
    this.emitPlayerStatus(PlayerStatus.PlaylistChanged, {
      playlist: fragments,
      holes: getAllFragmentHoles(fragments),
    });
    this.emitPlayerStatus(CameraStatus.Connected);
  }

  /**
   * Handle FRAG_LOADED player event
   * @param {*} event
   * @param {*} data
   */
  onFragLoaded(event, data) {
    this.log('FRAG_LOADED ', event, data);
  }

  /**
   * Handle FRAG_CHANGED player event
   * @param {*} event
   * @param {*} data
   */
  onFragChanged(event, data) {
    this.lastFragChanged = data.frag;
    const prevFrag = this.lastFragBuffered;
    const frag = data.frag;
    const discontinuity = prevFrag && frag.cc !== prevFrag.cc;
    this.log('FRAG_CHANGED ', event, data, 'discontinuity: ', discontinuity);
    this.lastFragChangeTime = performance.now();
    this.levelLoadNoFragChangeCount = 0;
    this.emitPlayerStatus(PlayerStatus.FragmentChanged, {
      frag,
      prevFrag,
      startPosition: this.player.startPosition,
    });
  }

  /**
   * Handle ERROR player event
   * @param {*} event
   * @param {*} data
   * @returns
   */
  async onError(event, data) {
    const { type, details, fatal, frag } = data;
    this.error(`HLS ERROR: ${type} ${details}`, `Fatal: ${fatal}`, data, event);

    if (details === Hls.ErrorDetails.LEVEL_LOAD_ERROR) {
      this.error('[LEVEL_LOAD_ERROR] Attempting recovery ', data);
      const errorResponse = await checkFragLoadLevelError(data);
      if (errorResponse === PlayerStatus.StreamingSessionExpired) {
        this.emitPlayerStatus(PlayerStatus.StreamingSessionExpired, {
          frag: data,
        });
        return;
      }
    }

    if (details === Hls.ErrorDetails.FRAG_LOAD_ERROR && frag.start) {
      this.error('[FRAG_LOAD_ERROR] Attempting recovery ', data);
      const errorResponse = await checkFragLoadLevelError(data);

      if (errorResponse === PlayerStatus.CodecIsNotTheSame) {
        this.emitPlayerStatus(PlayerStatus.CodecIsNotTheSame, data);
      }
      if (errorResponse === PlayerStatus.StreamingSessionExpired) {
        this.emitPlayerStatus(PlayerStatus.StreamingSessionExpired, data);
      }
      return;
    }

    if (details === Hls.ErrorDetails.FRAG_LOAD_TIMEOUT) {
      this.log('Attempting recovery', data);
      this.player.recoverMediaError();
      this.playerElement.currentTime = frag.start + frag.duration + 0.1;
      return;
    }

    if (fatal) {
      switch (type) {
        case Hls.ErrorTypes.NETWORK_ERROR:
          this.emitPlayerStatus(CameraStatus.Error, {
            errorCode: CommonErrorCodes.NETWORK_ERROR,
          });
          this.error('fatal network error encountered, try to recover');
          this.player.startLoad();
          break;
        case Hls.ErrorTypes.MEDIA_ERROR:
          this.handleMediaError(data);
          break;
        default:
          this.player.destroy();
          break;
      }
    } else if (type === Hls.ErrorTypes.NETWORK_ERROR) {
      this.error('HLS Network Error: ' + details);
      this.emitPlayerStatus(CameraStatus.Error, {
        errorCode: CommonErrorCodes.NETWORK_ERROR,
      });
    }
  }

  /**
   * Emit `playerStatus` event
   * @param {*} status
   * @param {*} payload
   */
  emitPlayerStatus(status, payload = {}) {
    EventBus.emit(this.eventKey, {
      type: 'playerStatus',
      payload: { status, ...payload },
      meta: { id: this.id },
    });
  }

  /**
   * Setup player
   * @param {*} playerElement
   * @param {*} eventKey
   * @param {*} id
   * @param {*} startPosition
   */
  setupPlayer(playerElement, eventKey, id, startPosition) {
    this.playerElement = playerElement;
    this.playerElement.addEventListener('error', this.handlePlayerElementError);
    this.id = id;
    this.eventKey = eventKey;

    if (!Hls.isSupported()) throw new Error('HLS.js is not supported.');
    if (this.player) {
      this.player.stopLoad();
      this.player.detachMedia();
      this.player.destroy();
    }

    // Create the Hls player
    this.player = new Hls({
      ...hlsConfig,
      pLoader: pLoader,
      autoStartLoad: !startPosition,
      debug: enableLogs(hlsConfig.debug, this.camera.id, this.camera.name),
    });
    this.player.currentLevel = 0;
  }
  /**
   * Get fragment list
   */
  getCurrentFragmentList() {
    const currentLevel = Math.max(this.player?.currentLevel || 0, 0);
    if (
      !this.player?.levels?.length ||
      !this.player?.levels?.[currentLevel]?.details
    ) {
      return [];
    }
    const { fragments } = this.player.levels[currentLevel].details;

    return mapFragments(fragments);
  }

  /**
   * Get parsed fragment list
   */
  getParsedFragmentList() {
    const fragments = this.getCurrentFragmentList();
    return fragments;
  }

  /**
   * Get the fragment after current fragment playing
   */
  getNextFragment() {
    const currentFragSn = this.lastFragChanged?.sn;
    if (!currentFragSn) {
      return null;
    }
    const fragments = this.getCurrentFragmentList();
    return fragments[currentFragSn];
  }

  /**
   * Call `play` video element with handling error
   */
  playPlayer() {
    const video = this.playerElement;
    if (!video) {
      console.warn('[WARN] video element does not exist.');
      return;
    }
    const playPromise = video.play();
    if (playPromise !== undefined) {
      playPromise.catch((error) => {
        console.log(error);
      });
    }
  }

  /**
   * Handle video player element error
   */
  handlePlayerElementError() {
    this.error(
      `[cam:${this.camera.name}] error code:`,
      this.playerElement.error.code +
        '; details: ' +
        this.playerElement.error.message
    );

    if (this.isPipelineError()) {
      this.handlePipelineError();
    }
  }

  /**
   * Whether video player element has `PIPELINE_ERROR_DECODE` error or not
   */
  isPipelineError() {
    const errorCode = this.playerElement?.error?.code;
    const errorMessage = this.playerElement?.error?.message || '';
    const isChromeVDA4 =
      errorCode === 3 && errorMessage.includes('PIPELINE_ERROR_DECODE');

    const isSafariVDA4 =
      errorCode === 3 &&
      errorMessage === 'Media failed to decode' &&
      this.device?.browser === 'Safari';
    return isChromeVDA4 || isSafariVDA4;
  }

  /**
   * Handle `PIPELINE_ERROR_DECODE` with skipping current fragment and renewing session
   * It retries 3 times withing `VDA4ErrorMaxDuration`. If more than 3 times it will return `No Data` error.
   */
  handlePipelineError() {
    const now = self.performance.now();
    if (
      !this.firstRecoverVDAError4 ||
      (this.firstRecoverVDAError4 &&
        now - this.firstRecoverVDAError4 > VDA4ErrorMaxDuration)
    ) {
      this.firstRecoverVDAError4 = self.performance.now();
      this.VDAError4Count = 0;
    }
    this.VDAError4Count += 1;
    if (
      this.firstRecoverVDAError4 &&
      now - this.firstRecoverVDAError4 < VDA4ErrorMaxDuration &&
      this.VDAError4Count > 3
    ) {
      this.error(
        `Error "PIPELINE_ERROR_DECODE" occurred ${
          this.VDAError4Count
        } times in ${
          VDA4ErrorMaxDuration / 1000
        } seconds but no recovery. There is no more data to display.`
      );
      EventBus.emit(this.eventKey, {
        type: 'playerStatus',
        payload: { status: CameraStatus.NoData },
        meta: { id: this.id },
      });
      return;
    }
    const nextFragment = this.getNextFragment();
    this.error(
      `Error "PIPELINE_ERROR_DECODE" occurred ${this.VDAError4Count} ${
        this.VDAError4Count > 1 ? 'times' : 'time'
      } at ${
        this.lastFragChanged
          ? dayjs(this.lastFragChanged.programDateTime)
              .add(this.lastFragChanged.duration, 'seconds')
              .format('HH:mm:ss')
          : dayjs().format('HH:mm:ss')
      } .
        We will proceed recovery after ${
          this.recoverVDAError4Delay / 1000
        } seconds.`
    );
    if (this.recoverVDAError4Timeout) {
      clearTimeout(this.recoverVDAError4Timeout);
    }

    this.recoverVDAError4Timeout = setTimeout(() => {
      EventBus.emit(this.eventKey, {
        type: 'playerStatus',
        payload: {
          status: PlayerStatus.CanNotRecover,
          frag: nextFragment,
        },
        meta: { id: this.id },
      });
    }, this.recoverVDAError4Delay);
  }

  /**
   * Handle `hls.js` media error and recover automatically
   */
  handleMediaError(data) {
    if (this.autoRecoverError) {
      if (this.isPipelineError()) {
        // this error should be handled by video element event listeners because sometimes `hls.js` will pause and does not catch this error
        return;
      }
      const now = self.performance.now();

      if (
        !this.recoverDecodingErrorDate ||
        now - this.recoverDecodingErrorDate > 3000
      ) {
        this.recoverDecodingErrorDate = self.performance.now();
        this.error('Trying to recover media error.');
        this.player.recoverMediaError();
        setTimeout(() => {
          this.playPlayer();
        }, 10);
      } else {
        if (
          (!this.recoverSwapAudioCodecDate ||
            now - this.recoverSwapAudioCodecDate > 3000) &&
          !(this.retrySwapAudioCodecCount > 2)
        ) {
          this.recoverSwapAudioCodecDate = self.performance.now();
          this.error('Trying to swap audio codec and recover media error.');
          this.player.swapAudioCodec();
          this.player.recoverMediaError();
          setTimeout(() => {
            this.playPlayer();
          }, 10);
          this.retrySwapAudioCodecCount += 1;
        } else {
          this.error(
            'Cannot recover. Last media error recovery failed.',
            data.frag
          );
          EventBus.emit(this.eventKey, {
            type: 'playerStatus',
            payload: {
              status: PlayerStatus.CanNotRecover,
              frag: this.lastFragChanged,
            },
            meta: { id: this.id },
          });
        }
      }
    }
  }

  /**
   * Stop load HLS.js player
   */
  stopLoad() {
    if (this.player) {
      this.player.stopLoad();
    }
  }

  /**
   * Start load HLS.js player
   */
  startLoad() {
    if (this.player) {
      this.player.startLoad();
    }
  }

  /**
   * Destroy HLS.js player
   */
  destroyPlayer() {
    if (this.player) {
      this.player.stopLoad();
      this.player.detachMedia();
      this.player.destroy();
      this.player = null;

      this.recoverSwapAudioCodecDate = null;
      this.recoverDecodingErrorDate = null;
      this.retrySwapAudioCodecCount = 0;

      clearTimeout(this.recoverVDAError4Timeout);
      clearTimeout(this.noFragChangeTimeout);
    }
  }

  /**
   * Destroy/dispose `hls.js`
   */
  destroy() {
    if (this.playerElement) {
      this.playerElement.removeEventListener(
        'error',
        this.handlePlayerElementError
      );
    }
    this.destroyPlayer();
  }
  dispose(reason) {
    tracer('[kv.service]/dispose', { reason });
    AppAbortController.abort(this.signalKey);
  }
}
